import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Subject } from 'rxjs';
import { TokenPermissionService } from './token-permission.service';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';

export type TOKEN_PROPD = {
  status: boolean,
  token?: string,
  expiration_date?: Date
}

@Component({
  selector: 'tokens',
  templateUrl: './token-permission.component.html',
  styleUrl: './token-permission.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'tokens',
  standalone: true,
  imports: [MatButtonModule, MatIconModule, NgTemplateOutlet, MatProgressBarModule],
})
export class TokenPermissionComponent implements OnInit, OnDestroy {

  @ViewChild('tokenOrigin') private _tokenOrigin: MatButton;
  @ViewChild('tokenPanel') private _tokenPanel: TemplateRef<any>;

  public mode: 'view' | 'modify' | 'add' | 'edit' = 'view';
  public loading: boolean = false;
  public tokenResult: TOKEN_PROPD = {status: false};

  private _overlayRef: OverlayRef;
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  constructor(
      private _changeDetectorRef: ChangeDetectorRef,
      private _overlay: Overlay,
      private _viewContainerRef: ViewContainerRef,
      private _tokenService: TokenPermissionService
  ) {}

  ngOnInit(): void {
    this._tokenService.token$.subscribe({
      next: (result: any) => {
        if (result) {
          console.log(result);
          this.tokenResult = result;
        } else {
          this.tokenResult.status = false;
        }
        this._changeDetectorRef.markForCheck();
      }
    })
  }

  openPanel(): void {
      // Return if the shortcuts panel or its origin is not defined
      if (!this._tokenPanel || !this._tokenOrigin) {
          return;
      }

      // Make sure to start in 'view' mode
      this.mode = 'view';

      // Create the overlay if it doesn't exist
      if (!this._overlayRef) {
          this._createOverlay();
      }

      // Attach the portal to the overlay
      this._overlayRef.attach(
          new TemplatePortal(this._tokenPanel, this._viewContainerRef)
      );
  }

  closePanel(): void {
      this._overlayRef.detach();
  }

  generateToken() {
    this.loading = true;
    this._changeDetectorRef.markForCheck();
    this._tokenService.generateToken().subscribe({
      complete: () => {
        this.loading = false;
        this._changeDetectorRef.markForCheck();
      },
    });
  }

  ngOnDestroy(): void {
      this._unsubscribeAll.next(null);
      this._unsubscribeAll.complete();
  }

  private _createOverlay(): void {
      // Create the overlay
      this._overlayRef = this._overlay.create({
          hasBackdrop: true,
          backdropClass: 'fuse-backdrop-on-mobile',
          scrollStrategy: this._overlay.scrollStrategies.block(),
          positionStrategy: this._overlay
              .position()
              .flexibleConnectedTo(
                  this._tokenOrigin._elementRef.nativeElement
              )
              .withLockedPosition(true)
              .withPush(true)
              .withPositions([
                  {
                      originX: 'start',
                      originY: 'bottom',
                      overlayX: 'start',
                      overlayY: 'top',
                  },
                  {
                      originX: 'start',
                      originY: 'top',
                      overlayX: 'start',
                      overlayY: 'bottom',
                  },
                  {
                      originX: 'end',
                      originY: 'bottom',
                      overlayX: 'end',
                      overlayY: 'top',
                  },
                  {
                      originX: 'end',
                      originY: 'top',
                      overlayX: 'end',
                      overlayY: 'bottom',
                  },
              ]),
      });

      // Detach the overlay from the portal on backdrop click
      this._overlayRef.backdropClick().subscribe(() => {
          this._overlayRef.detach();
      });
  }
}
